import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/config';

/**
 * TrialHourCountdown — small floating pill shown site-wide while the paid
 * 60-minute trial pass is active. Polls the backend once at mount for the
 * remaining seconds, then ticks locally to avoid extra requests.
 *
 * Copy is intentionally minimal (user asked for "only a small side timer").
 */

const LABEL = {
  sv: 'Provåtkomst',
  ar: 'دقيقة تجربة',
  en: 'Trial',
  tr: 'Deneme',
  fa: 'دسترسی آزمایشی',
  so: 'Tijaabo',
  pl: 'Próba',
  es: 'Prueba',
  de: 'Test',
};

function format(seconds) {
  if (seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function TrialHourCountdown({ lang = 'sv' }) {
  const { isAuthenticated, user, token } = useAuth() || {};
  const [secondsLeft, setSecondsLeft] = useState(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    let cancelled = false;
    const load = () => axios
      .get(`${API}/payments/trial-hour/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled) setSecondsLeft(r.data?.active ? r.data.seconds_left : null); })
      .catch(() => {});
    load();
    // Re-sync with backend every 5 min so the pill can't drift.
    const resync = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(resync); };
  }, [isAuthenticated, token, user?.id]);

  useEffect(() => {
    if (secondsLeft == null || secondsLeft <= 0) return;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s == null) return s;
        if (s <= 1) {
          clearInterval(tickRef.current);
          // Trigger a soft reload so the paywall reappears once the
          // hour ends. Keeps the UX honest without a socket connection.
          setTimeout(() => window.location.reload(), 600);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => tickRef.current && clearInterval(tickRef.current);
  }, [secondsLeft]);

  if (secondsLeft == null || secondsLeft <= 0) return null;

  const label = LABEL[lang] || LABEL.sv;
  return (
    <div
      className="fixed bottom-24 end-4 z-40 select-none rounded-full bg-gradient-to-r from-cyan-600 to-sky-600 text-white shadow-lg shadow-cyan-500/30 px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 border border-white/30"
      data-testid="trial-hour-countdown"
    >
      <Clock className="w-3.5 h-3.5" />
      <span>{label}</span>
      <span className="tabular-nums">{format(secondsLeft)}</span>
    </div>
  );
}
